import React from 'react';
import ColorPicker from './ColorPicker';
import { Input, Select } from '../Common';
import { FONT_OPTIONS, FONT_OPTIONS_INHERIT } from './fontOptions';
import { resolveHeaderFooterLayout, headerFooterZoneWidths, HF_ZONE_WIDTHS, HF_LINK_COLOR } from './headerFooterDefaults';

/**
 * Editor for a header or footer.
 *
 * Two halves, matching what the renderers produce:
 *
 *   Zones  — one row split left | centre | right. This is the common case and
 *            the reason it exists: a report header is a logo, a title and a
 *            page number on one line at three justifications, which a vertical
 *            list of components cannot express.
 *   Blocks — anything stacked beneath that row, for the rest.
 *
 * A template saved before this existed has no `zones`; the backend maps its
 * flat fields onto the same arrangement it already rendered (logo left, text
 * centre, page number right), so opening one here shows what it already looks
 * like rather than an empty editor.
 */

const PART_TYPES = [
  { value: 'none', label: '— Empty —' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'pageNumber', label: 'Page number' },
];

const BLOCK_TYPES = [...PART_TYPES.filter(t => t.value !== 'none'), { value: 'rule', label: 'Horizontal rule' }];


const boxStyle = {
  border: '1px solid var(--border)', borderRadius: 6,
  padding: 8, background: 'var(--bg-input)',
};

function readImage(file, onLoad) {
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) { alert('Image must be under 2MB'); return; }
  const reader = new FileReader();
  reader.onload = () => onLoad(reader.result);
  reader.readAsDataURL(file);
}

/**
 * Font, size, colour and weight for a text-bearing part.
 *
 * Shared by text and page numbers because both are runs of text in the
 * document, and a header whose title could be branded while its page number
 * stayed grey would read as an oversight. Blank font and size mean "use the
 * header's own", so changing the header default still carries.
 */
function TextStyleRow({ part, onChange, compact }) {
  const set = (k, v) => onChange({ ...part, [k]: v });
  const fs = compact ? 10 : 11;
  const toggle = (k, label, style) => (
    <button type="button" title={label}
      onClick={() => set(k, !part[k])}
      style={{ width: 22, height: 22, lineHeight: '20px', padding: 0, cursor: 'pointer',
               borderRadius: 3, border: '1px solid var(--border)', fontSize: fs, ...style,
               background: part[k] ? 'var(--primary)' : 'var(--bg-card)',
               color: part[k] ? '#fff' : 'var(--text-secondary)' }}>
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
      <select value={part.fontFamily || ''} onChange={e => set('fontFamily', e.target.value || null)}
        title="Font" style={{ flex: '1 1 92px', minWidth: 0, padding: '2px 4px', fontSize: fs,
                 borderRadius: 3, border: '1px solid var(--border)',
                 background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit' }}>
        {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      <input type="number" min="5" max="24" value={part.fontSize || ''} placeholder="pt"
        title="Size in points — blank uses the header's own size"
        onChange={e => set('fontSize', e.target.value ? parseInt(e.target.value) : null)}
        style={{ width: 42, padding: '2px 4px', fontSize: fs, borderRadius: 3,
                 border: '1px solid var(--border)', background: 'var(--bg-card)',
                 color: 'var(--text)', fontFamily: 'inherit' }} />
      <ColorPicker label="Text colour" size={22} allowNone={false}
        value={part.color || '#999999'} onChange={c => set('color', c || '#999999')} />
      {toggle('bold', 'B', { fontWeight: 700 })}
      {toggle('italic', 'I', { fontStyle: 'italic' })}
      {toggle('underline', 'U', { textDecoration: 'underline' })}
    </div>
  );
}

/** The settings for one zone or block, by type. */
function PartFields({ part, onChange, allowTemplateLogo }) {
  const set = (k, v) => onChange({ ...part, [k]: v });

  if (part.type === 'text') {
    const lines = String(part.text || '').split('\n').length;
    const hasLink = /(https?:\/\/|www\.)/i.test(String(part.text || ''));
    return (
      <>
        {/* A textarea, not an input: several lines in ONE part is what lets a
            logo in the left zone sit beside all of them. Separate stacked
            blocks push the logo onto a row of its own above the text. */}
        <textarea value={part.text || ''} rows={Math.min(6, Math.max(2, lines))}
          placeholder={'Text — one line per line.\n%CLIENTNAME% etc. resolve. A URL becomes a link.'}
          onChange={e => set('text', e.target.value)}
          style={{ width: '100%', padding: '5px 8px', fontSize: 12, borderRadius: 4, boxSizing: 'border-box',
                   border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)',
                   fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
          Report variables resolve here. Each line prints on its own line, and any
          http:// or www. address becomes a clickable link.
        </div>
        <TextStyleRow part={part} onChange={onChange} />
        {hasLink && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
            <ColorPicker label="Link colour" size={22} allowNone={false}
              value={part.linkColor || HF_LINK_COLOR}
              onChange={c => set('linkColor', c || HF_LINK_COLOR)} />
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              Links are underlined and take this colour, not the text colour above.
            </span>
          </div>
        )}
      </>
    );
  }

  if (part.type === 'image') {
    const src = part.imageData;
    return (
      <>
        {src ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
            <img src={src} alt="" style={{ maxHeight: 26, maxWidth: 90, border: '1px solid var(--border)', borderRadius: 3 }} />
            <button type="button" onClick={() => onChange({ ...part, imageData: null })}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 3, cursor: 'pointer', color: '#ef4444', fontSize: 10, padding: '1px 6px' }}>
              Remove
            </button>
          </div>
        ) : allowTemplateLogo ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginBottom: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={part.useTemplateLogo !== false}
              onChange={e => set('useTemplateLogo', e.target.checked)} />
            Use the template logo
          </label>
        ) : null}
        <input type="file" accept="image/*" style={{ fontSize: 10, width: '100%' }}
          onChange={e => { readImage(e.target.files[0], d => onChange({ ...part, imageData: d })); e.target.value = ''; }} />
        <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
          <Input label="W (px)" type="number" min="10" max="600" value={part.width || 60}
            onChange={e => set('width', parseInt(e.target.value) || 60)} style={{ marginBottom: 0, flex: 1 }} />
          <Input label="H (px)" type="number" min="10" max="300" value={part.height || 20}
            onChange={e => set('height', parseInt(e.target.value) || 20)} style={{ marginBottom: 0, flex: 1 }} />
        </div>

        {/* Floating.
            A Word header puts each part on its own line or in its own table
            cell, so an image and a line of text can sit beside each other but
            never in the same place. Floating takes the image out of the text
            flow and anchors it to the page margins instead, which is what lets
            a wordmark sit behind a title rather than above it. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 7, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!part.float}
            onChange={e => set('float', e.target.checked)} />
          Float behind text
        </label>

        {part.float && (
          <div style={{ marginTop: 5, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 5 }}>
              <Select label="Across" value={part.hAlign || 'left'}
                onChange={e => set('hAlign', e.target.value)}
                options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' },
                          { value: 'right', label: 'Right' }]}
                style={{ marginBottom: 0, flex: 1 }} />
              <Select label="Down" value={part.vAlign || 'top'}
                onChange={e => set('vAlign', e.target.value)}
                options={[{ value: 'top', label: 'Top' }, { value: 'center', label: 'Middle' },
                          { value: 'bottom', label: 'Bottom' }]}
                style={{ marginBottom: 0, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
              <Input label="Nudge X (px)" type="number" value={part.hOffsetPx || 0}
                onChange={e => set('hOffsetPx', parseInt(e.target.value) || 0)}
                style={{ marginBottom: 0, flex: 1 }} />
              <Input label="Nudge Y (px)" type="number" value={part.vOffsetPx || 0}
                onChange={e => set('vOffsetPx', parseInt(e.target.value) || 0)}
                style={{ marginBottom: 0, flex: 1 }} />
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
              Measured from the page margin. A non-zero nudge replaces the
              dropdown on that axis rather than adding to it, because Word
              takes an alignment or an exact position, not both.
            </div>
          </div>
        )}
      </>
    );
  }

  if (part.type === 'pageNumber') {
    return (
      <>
        <Select value={part.format || 'ofTotal'} onChange={e => set('format', e.target.value)}
          options={[{ value: 'current', label: 'Page N' }, { value: 'ofTotal', label: 'Page N of M' }]}
          style={{ marginBottom: 0 }} />
        <TextStyleRow part={part} onChange={onChange} />
      </>
    );
  }

  if (part.type === 'rule') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ColorPicker label="Rule colour" size={26}
          value={part.color || '#cccccc'} onChange={c => set('color', c || '#cccccc')} />
        <Input label="Thickness" type="number" min="1" max="6" value={part.thickness || 1}
          onChange={e => set('thickness', parseInt(e.target.value) || 1)} style={{ marginBottom: 0, flex: 1 }} />
      </div>
    );
  }

  return null;
}

function Zone({ label, part, onChange, allowTemplateLogo }) {
  const value = part || { type: 'none' };
  return (
    <div style={{ ...boxStyle, flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: 'var(--text-muted)', marginBottom: 5 }}>{label}</div>
      <Select value={value.type || 'none'} options={PART_TYPES} style={{ marginBottom: 6 }}
        onChange={e => onChange(e.target.value === 'none' ? null : { ...value, type: e.target.value })} />
      {value.type && value.type !== 'none' && (
        <PartFields part={value} onChange={onChange} allowTemplateLogo={allowTemplateLogo} />
      )}
    </div>
  );
}

export default function HeaderFooterEditor({ config, onUpdate, kind, wide }) {
  // The shared resolver, not a copy of it. This was a hand-duplicated version
  // of resolveHeaderFooterLayout that no parity check covered, so the editor
  // and the renderers could disagree about what an old template looks like
  // without anything noticing.
  const { zones, blocks } = resolveHeaderFooterLayout(config, kind);
  const widths = headerFooterZoneWidths(config);

  const setZone = (key, part) => onUpdate({ zones: { ...zones, [key]: part } });
  const setBlocks = next => onUpdate({ zones, blocks: next });
  const setWidth = (key, value) => {
    const n = parseInt(value, 10);
    onUpdate({ zoneWidths: { ...widths, [key]: Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0 } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, maxWidth: wide ? 420 : undefined }}>
        <Select label="Font" value={config.fontFamily || 'Helvetica'} options={FONT_OPTIONS}
          onChange={e => onUpdate({ fontFamily: e.target.value })} style={{ marginBottom: 0, flex: 2 }} />
        <Input label="Size (pt)" type="number" min="5" max="24" value={config.fontSize || 8}
          onChange={e => onUpdate({ fontSize: parseInt(e.target.value) || 8 })} style={{ marginBottom: 0, flex: 1 }} />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
                      color: 'var(--text-secondary)', cursor: 'pointer' }}>
        <input type="checkbox" checked={config.hideOnFirstPage === true}
          onChange={e => onUpdate({ hideOnFirstPage: e.target.checked })}
          style={{ cursor: 'pointer', margin: 0 }} />
        Do not show on the first page
      </label>

      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 5 }}>
          Row — left, centre and right
        </div>
        {!wide && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6 }}>
            These three sit on one line in the report; they are stacked here only because the panel is narrow.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: 6, alignItems: 'stretch' }}>
          <Zone label="Left" part={zones.left} onChange={p => setZone('left', p)} allowTemplateLogo={kind === 'header'} />
          <Zone label="Centre" part={zones.center} onChange={p => setZone('center', p)} allowTemplateLogo={kind === 'header'} />
          <Zone label="Right" part={zones.right} onChange={p => setZone('right', p)} allowTemplateLogo={kind === 'header'} />
        </div>

        {/* Column widths.
            The row was fixed equal thirds, which suits "logo | title | page
            number" and nothing else: a one-line accreditation statement in a
            third of the width wraps to three lines with two thirds of the row
            empty beside it. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginTop: 7 }}>
          <Input label="Left %" type="number" min="0" max="100" value={widths.left}
            onChange={e => setWidth('left', e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
          <Input label="Centre %" type="number" min="0" max="100" value={widths.center}
            onChange={e => setWidth('center', e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
          <Input label="Right %" type="number" min="0" max="100" value={widths.right}
            onChange={e => setWidth('right', e.target.value)} style={{ marginBottom: 0, flex: 1 }} />
          <button type="button" onClick={() => onUpdate({ zoneWidths: { ...HF_ZONE_WIDTHS } })}
            title="Back to equal thirds"
            style={{ padding: '7px 10px', fontSize: 11, borderRadius: 'var(--radius)', whiteSpace: 'nowrap',
                     background: 'var(--bg-input)', color: 'var(--text-secondary)',
                     border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Even
          </button>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
          Rescaled to total 100, so 10 / 80 / 10 and 1 / 8 / 1 lay out the same.
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Below the row</span>
          <div style={{ display: 'flex', gap: 5 }}>
            <button type="button" onClick={() => setBlocks([...blocks, { type: 'rule', align: 'left', color: '#cccccc', thickness: 1 }])}
              title="A horizontal line across the full width"
              style={{ padding: '3px 9px', fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius)',
                       background: 'var(--bg-input)', color: 'var(--text-secondary)',
                       border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
              + Line
            </button>
            <button type="button" onClick={() => setBlocks([...blocks, { type: 'text', align: 'center', text: '' }])}
              style={{ padding: '3px 9px', fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius)',
                       background: 'var(--bg-input)', color: 'var(--text-secondary)',
                       border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
              + Text
            </button>
            {/* Image was already a valid block type and reachable only by
                adding a Text block and changing its dropdown. A button beside
                the other two is how anyone finds it. */}
            <button type="button" onClick={() => setBlocks([...blocks, { type: 'image', align: 'left', width: 60, height: 20 }])}
              title="A picture. Can be floated behind the text so both share a line."
              style={{ padding: '3px 9px', fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius)',
                       background: 'var(--bg-input)', color: 'var(--text-secondary)',
                       border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
              + Image
            </button>
          </div>
        </div>
        {blocks.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Nothing yet. A line sits across the full width beneath the row; text adds another line of its own;
            an image can be floated behind the text so the two share a line.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {blocks.map((b, i) => (
              <div key={i} style={boxStyle}>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 6 }}>
                  <Select value={b.type} options={BLOCK_TYPES} style={{ marginBottom: 0, flex: 1 }}
                    onChange={e => setBlocks(blocks.map((x, n) => n === i ? { ...x, type: e.target.value } : x))} />
                  <Select value={b.align || 'left'} style={{ marginBottom: 0, flex: 1 }}
                    options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' },
                              { value: 'right', label: 'Right' }, { value: 'justify', label: 'Justify' }]}
                    onChange={e => setBlocks(blocks.map((x, n) => n === i ? { ...x, align: e.target.value } : x))} />
                  <button type="button" title="Remove" onClick={() => setBlocks(blocks.filter((_, n) => n !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 15, padding: '0 4px' }}>×</button>
                </div>
                <PartFields part={b} allowTemplateLogo={false}
                  onChange={np => setBlocks(blocks.map((x, n) => n === i ? np : x))} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
